import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../i18n/config";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";

import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import "./PatientPlan.css";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../../components/ui/DateInput";
import Toast from "../../../components/layout/toast/Toast";
import { Modal, Button, Form } from "react-bootstrap";
import SelectModal from "../../../components/layout/selectModal/SelectModal";
import SearchSelectModal from "../../../components/layout/searchSelectModal/SearchSelectModal";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import PaidModal from "./PaidModal";
import PatientPlanMatrix from "./PatientPlanMatrix";
import { useUserSettings } from "../../../context/UserSettingsContext";
import { localizedSuffix } from "../../../i18n/localizedService";

/**
 * Название услуги с приоритетом языка интерфейса.
 *
 * Записи плана приходят с полями `name` (русское) и `name_en` (английский
 * перевод). На en/ar показываем name_en, но если перевод не заполнен —
 * русское название. На ru — наоборот.
 */
const pickLocalizedValue = (record, key, language) => {
  const suffix = localizedSuffix(language);
  const primary = record?.[`${key}${suffix}`]?.value;
  if (primary != null && String(primary).trim() !== "") return primary;

  const secondary = record?.[suffix ? key : `${key}_en`]?.value;
  if (secondary != null && String(secondary).trim() !== "") return secondary;

  return "";
};

const ALLERGY_ANAMNESIS_KEYS = [
  "medications",
  "cosmetics",
  "food",
  "household",
  "plants",
  "sun",
  "cold",
];

const ALLERGY_ANAMNESIS_FIELD_KEYS = {
  medications: "plan.allergy.medications",
  cosmetics: "plan.allergy.cosmetics",
  food: "plan.allergy.food",
  household: "plan.allergy.household",
  plants: "plan.allergy.plants",
  sun: "plan.allergy.sun",
  cold: "plan.allergy.cold",
};

const parseAllergyAnamnesisRaw = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch (_) {
    return null;
  }
  return null;
};

const formatAllergyAnamnesisLine = (raw, t) => {
  const source = parseAllergyAnamnesisRaw(raw);
  if (!source) return "";
  return ALLERGY_ANAMNESIS_KEYS.map((key) => {
    const item = source[key];
    if (!item || typeof item !== "object" || Array.isArray(item)) return "";
    if (item.answer !== "yes") return "";
    const note = String(item.note ?? "")
      .replace(/\s+/g, " ")
      .trim();
    const label = t(ALLERGY_ANAMNESIS_FIELD_KEYS[key]);
    return note ? `${label} (${note})` : label;
  })
    .filter(Boolean)
    .join("; ");
};

function PatientPlan() {
  const { t, i18n } = useTranslation("patient");
  const locale = getLocale(i18n.language);
  // Отображаемое название услуги: язык интерфейса, при пустом переводе — другой язык.
  const displayName = (record) => pickLocalizedValue(record, "name", i18n.language);
  const { get, update } = useUserSettings();
  const [loading, setLoading] = useState(true); // только для первичной загрузки/смены пациента
  const [error, setError] = useState(null);
  const { patientId, planId: routePlanId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { planGroupsSlotEl } = useOutletContext() || {};
  const [patient, setPatient] = useState(null);
  const [allergyAnamnesisRaw, setAllergyAnamnesisRaw] = useState(null);
  const allergyAnamnesisLine = React.useMemo(
    () => formatAllergyAnamnesisLine(allergyAnamnesisRaw, t),
    [allergyAnamnesisRaw, t],
  );
  const [changed, setChanged] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [toast, setToast] = useState({});

  // Чтобы при перерендере (после запроса) не "прыгало" вверх: сохраняем и восстанавливаем scrollY
  const restoreScrollPendingRef = useRef(false);
  const lastScrollYRef = useRef(0);
  // Автовход в режим редактирования (?edit=1) выполняется один раз для конкретного плана
  const autoEditAppliedRef = useRef(null);

  const [showModalServices, setShowModalServices] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [serviceSelectMode, setServiceSelectMode] = useState("add_service"); // add_service | add_subitem
  const [selectedParentItemId, setSelectedParentItemId] = useState(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [serviceSearchLoading, setServiceSearchLoading] = useState(false);
  const [serviceSearchResults, setServiceSearchResults] = useState([]);
  const [serviceSearchError, setServiceSearchError] = useState(null);

  // Фильтр услуг по филиалу в модальном окне выбора услуги.
  const [serviceFilials, setServiceFilials] = useState([]);
  const [serviceMyClinics, setServiceMyClinics] = useState([]);
  const [serviceClinicId, setServiceClinicId] = useState("");

  const [loadingGroup, setLoadingGroup] = useState(false);
  const [availableGroup, setAvailableGroup] = useState([]);
  const [showModalGroup, setShowModalGroup] = useState(false);
  const [selectedGroupIdToAdd, setSelectedGroupIdToAdd] = useState("");

  const [loadingService, setLoadingService] = useState(false);
  const [showModalService, setShowService] = useState(false);
  const [paramsService, setParamsService] = useState([]);

  const [showModalPay, setShowModalPay] = useState(false);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [payModalStaff, setPayModalStaff] = useState([]);
  const [paymentGroupId, setPaymentGroupId] = useState(null);

  // Режим отображения курсов
  const [coursesMode, setCoursesMode] = useState(false);
  // Выбор услуг для курсов (чекбоксы по услугам)
  const [courseSelected, setCourseSelected] = useState([]);
  const [courseSelectedName, setCourseSelectedName] = useState("");
  const [courseSelectedColor, setCourseSelectedColor] = useState("#fffae0");
  const [showModalAddCourse, setShowModalAddCourse] = useState(false);

  // Раскрывающееся меню «Добавить услугу»: из каталога / новую.
  // Храним id группы, для которой меню открыто (null — закрыто).
  const [addServiceMenuGroupId, setAddServiceMenuGroupId] = useState(null);
  // Модальное окно добавления своей (не из каталога) услуги в план лечения
  const [showModalCustomService, setShowModalCustomService] = useState(false);
  const [customServiceName, setCustomServiceName] = useState("");
  const [customServiceContext, setCustomServiceContext] = useState({
    groupType: null,
    groupId: null,
  });

  // Закрытие меню «Добавить услугу» по клику вне него
  useEffect(() => {
    if (addServiceMenuGroupId === null) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest(".btn-add-service-dropdown")) {
        setAddServiceMenuGroupId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addServiceMenuGroupId]);

  // Название плана лечения
  const [planName, setPlanName] = useState("");
  const [treatmentStartDate, setTreatmentStartDate] = useState("");

  // Предустановленные цвета для курса (HTML-формат #RRGGBB, очень бледные)
  const COURSE_COLOR_OPTIONS = [
    { labelKey: "plan.colors.paleYellow", value: "#fffae0" },
    { labelKey: "plan.colors.paleGreen", value: "#eaf9ea" },
    { labelKey: "plan.colors.paleBlue", value: "#d4ffff" },
    { labelKey: "plan.colors.palePink", value: "#ffdae0" },
    { labelKey: "plan.colors.paleOrange", value: "#ffeacb" },
    { labelKey: "plan.colors.paleLavender", value: "#e6e6fa" },
    { labelKey: "plan.colors.paleMint", value: "#056a0545" },
    { labelKey: "plan.colors.palePeach", value: "#ffdab996" },
    { labelKey: "plan.colors.paleSky", value: "#aadbf999" },
    { labelKey: "plan.colors.paleLilac", value: "#c793f77a" },
  ];

  const WITHOUT_APPOINTMENTS_GROUP_NAMES = new Set([
    "пероральные лекарственные препараты",
    "процедуры",
    "чекапы",
  ]);
  const WEEK_DAYS_FALLBACK = [];

  const weekDaysShort = React.useMemo(() => {
    const raw = String(t("plan.weekdaysShort") || "");
    const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return list.length ? list : WEEK_DAYS_FALLBACK;
  }, [t]);

  const normalizeGroupName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const isWithoutAppointmentsGroupByName = (name) =>
    WITHOUT_APPOINTMENTS_GROUP_NAMES.has(normalizeGroupName(name));

  const getItemBasePriceValue = (item) =>
    parseFloat(item?.amount_base?.value ?? item?.amount_base_total?.value ?? 0) || 0;

  const getGroupEffectiveTotal = (group) => {
    const backendTotal = parseFloat(group?.amount_total?.value);
    if (!isWithoutAppointmentsGroupByName(group?.name?.value)) {
      return Number.isFinite(backendTotal) ? backendTotal : 0;
    }

    return (group?.items || []).reduce(
      (sum, item) => sum + getItemBasePriceValue(item),
      0,
    );
  };

  const planEffectiveTotal = (patient?.result?.groups || []).reduce(
    (sum, group) => sum + getGroupEffectiveTotal(group),
    0,
  );
  const planTotalToDisplay =
    (patient?.result?.groups || []).length > 0
      ? planEffectiveTotal
      : parseFloat(patient?.result?.total_calculated_price?.value) || 0;

  // Модальное окно «Указать дату приёма»
  const [showSetDateModal, setShowSetDateModal] = useState(false);
  const [setDateParams, setSetDateParams] = useState({ appointmentNumber: null, groupId: null });
  const [showAppointmentCommentModal, setShowAppointmentCommentModal] = useState(false);
  const [appointmentCommentParams, setAppointmentCommentParams] = useState({
    appointmentNumber: null,
    groupId: null,
    itemId: null,
  });
  const [appointmentCommentValue, setAppointmentCommentValue] = useState("");
  const [showAppointmentCommentViewModal, setShowAppointmentCommentViewModal] = useState(false);
  const [appointmentCommentViewValue, setAppointmentCommentViewValue] = useState("");

  // Вид плана: таблица (по умолчанию) или схема-матрица
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("mis:plan-view") === "matrix" ? "matrix" : "table";
    } catch (_) {
      return "table";
    }
  });

  const changeViewMode = (nextMode) => {
    setViewMode(nextMode);
    try {
      localStorage.setItem("mis:plan-view", nextMode);
    } catch (_) {
      /* localStorage может быть недоступен */
    }
  };


  const fields = [
    { name: "name", label: t("plan.fields.name") },
    { name: "sort", label: t("plan.fields.sort"), type: "number" },
    { name: "amount_base", label: t("plan.fields.amountBase"), type: "number" },
    { name: "composition", label: t("plan.fields.composition"), type: "textarea" },
    { name: "technology", label: t("plan.fields.technology"), type: "textarea" },
  ];

  const isSuccessResponse = (data) =>
    Boolean(data) && data?.status !== "error" && data?.status !== "fail";

  const getCurrentPlanId = () => {
    const parsedRoutePlanId = Number(routePlanId);
    if (Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0) {
      return parsedRoutePlanId;
    }
    const fallbackPlanId = Number(patient?.result?.id?.value);
    return Number.isFinite(fallbackPlanId) && fallbackPlanId > 0 ? fallbackPlanId : 0;
  };

  const refreshCurrentPlan = async () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return null;
    try {
      const refreshedPlan = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
      });
      if (isSuccessResponse(refreshedPlan)) {
        setPatient(refreshedPlan);
        return refreshedPlan;
      }
      return null;
    } catch (err) {
      console.error("Ошибка перезагрузки текущего плана:", err);
      return null;
    }
  };

  const applyPlanResponse = async (data) => {
    if (!data || !data.result) return null;

    const parsedRoutePlanId = Number(routePlanId);
    const hasRoutePlanId = Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0;
    const responsePlanId = Number(data?.result?.id?.value);
    const hasResponsePlanId = Number.isFinite(responsePlanId) && responsePlanId > 0;

    if (hasRoutePlanId && hasResponsePlanId && parsedRoutePlanId !== responsePlanId) {
      const refreshedPlan = await refreshCurrentPlan();
      if (refreshedPlan) return refreshedPlan;
    }

    setPatient(data);
    return data;
  };

  const logPlanChange = async (
    key,
    { idItem = "", type = "", idClinic = 0, details = "" } = {},
  ) => {
    await logPatientChange({
      idPatient: patientId,
      idItem,
      type,
      idClinic,
      text: buildPatientLogText(key, { details }),
    });
  };

  const getServiceNameByItemId = (itemId) => {
    const allItems = (patient?.result?.groups || []).flatMap((group) => group?.items || []);
    const found = allItems.find((item) => String(item?.id?.value) === String(itemId));
    return found?.name?.value || "";
  };

  const getGroupNameById = (groupId) => {
    const found = (patient?.result?.groups || []).find(
      (group) => String(group?.id?.value) === String(groupId),
    );
    return found?.name?.value || "";
  };

  const getGroupAppointmentLimit = (group) => {
    const groupLimit = Number(group?.count_appointment?.value);
    if (Number.isFinite(groupLimit) && groupLimit > 0) {
      return groupLimit;
    }
    const planLimit = Number(patient?.result?.count_appointment?.value);
    return Number.isFinite(planLimit) && planLimit > 0 ? planLimit : 0;
  };

  const getAppointmentHeaderType = (group) => {
    const groupType = Number(group?.column_type?.value);
    if (Number.isFinite(groupType) && groupType >= 0 && groupType <= 4) {
      return groupType;
    }
    return 0;
  };

  const getAppointmentHeaderLabel = (appointmentNumber, appointmentHeaderType) => {
    const index = Math.max(appointmentNumber - 1, 0);
    const days = weekDaysShort.length ? weekDaysShort : WEEK_DAYS_FALLBACK;

    if (!days.length) {
      return t("plan.appointment", { number: appointmentNumber });
    }

    if (appointmentHeaderType === 2) {
      return days[index % days.length];
    }
    if (appointmentHeaderType === 3) {
      const even = days.filter((_, i) => (i + 1) % 2 === 0);
      return even.length ? even[index % even.length] : days[index % days.length];
    }
    if (appointmentHeaderType === 4) {
      const odd = days.filter((_, i) => (i + 1) % 2 !== 0);
      return odd.length ? odd[index % odd.length] : days[index % days.length];
    }

    return t("plan.appointment", { number: appointmentNumber });
  };

  const handleColumnTypeChange = async (group, nextTypeRaw) => {
    const groupId = Number(group?.id?.value) || 0;
    const nextType = Number(nextTypeRaw);
    if (!groupId) return;
    if (!Number.isFinite(nextType) || nextType < 0 || nextType > 4) return;
    if (blocked) return;

    const currentType = getAppointmentHeaderType(group);
    if (currentType === nextType) return;

    setBlocked(true);
    try {
      await fetchPlan({
        id_plan: patient?.result?.id?.value || 0,
        id_group: groupId,
        action: "column_type_group",
        value: nextType,
        showFullLoading: false,
      });
    } finally {
      setBlocked(false);
    }
  };

  useEffect(() => {
    // первичная загрузка для пациента — можно показывать full-page loading
    const parsedPlanId = Number(routePlanId);
    fetchPlan({
      id_plan: Number.isFinite(parsedPlanId) && parsedPlanId > 0 ? parsedPlanId : 0,
      showFullLoading: true,
    });
  }, [patientId, routePlanId]);

  // Автоматический вход в режим редактирования, если план открыт после создания (?edit=1).
  useEffect(() => {
    if (searchParams.get("edit") !== "1") return;
    if (!patient || !isSuccessResponse(patient)) return;

    const loadedPlanId = Number(patient?.result?.id?.value) || 0;
    const targetPlanId = Number(routePlanId) || loadedPlanId;
    if (autoEditAppliedRef.current === targetPlanId) return;

    autoEditAppliedRef.current = targetPlanId;
    setCoursesMode(false);
    setChanged(true);

    // Убираем параметр из URL, чтобы он не навязывал режим при обновлении/переходах.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("edit");
    setSearchParams(nextParams, { replace: true });
  }, [patient, routePlanId, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadAllergyAnamnesis = async () => {
      setAllergyAnamnesisRaw(null);
      if (!patientId) return;
      try {
        const docsListData = await GetData({
          module: "patient",
          block: "patient_docs",
          id_patient: patientId,
          action: "list",
        });
        const docs = Array.isArray(docsListData?.result) ? docsListData.result : [];
        const latestAnamnesis = docs
          .filter((doc) => Number(doc?.template_id) === 3)
          .sort((a, b) => {
            const da = Date.parse(a?.update_date || a?.created_date) || 0;
            const db = Date.parse(b?.update_date || b?.created_date) || 0;
            if (db !== da) return db - da;
            return (Number(b?.id) || 0) - (Number(a?.id) || 0);
          })[0];
        if (cancelled) return;
        setAllergyAnamnesisRaw(latestAnamnesis?.data?.allergy_anamnesis ?? null);
      } catch (err) {
        console.error("Ошибка загрузки аллергического анамнеза:", err);
      }
    };

    loadAllergyAnamnesis();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useLayoutEffect(() => {
    if (!restoreScrollPendingRef.current) return;
    restoreScrollPendingRef.current = false;

    if (typeof window === "undefined") return;
    const y = lastScrollYRef.current || 0;

    // 2 кадра — чтобы дождаться реального layout после обновления таблицы
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    });
  }, [patient]);

  const handleChangedOpen = () => {
    // При входе в режим редактирования выходим из режима курсов
    setCoursesMode(false);
    setChanged(true);
  };
  const handleChangedClose = () => {
    setChanged(false);
  };

  const handleCoursesOpen = () => {
    // При входе в режим курсов выходим из режима редактирования
    setChanged(false);
    setCoursesMode(true);
  };

  const handleCoursesClose = () => {
    setCoursesMode(false);
  };

  const handleToggleCourseItem = (itemId) => {
    setCourseSelected((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  /** Удаление услуги из курса. Аргумент: id услуги (service_id). */
  const handleRemoveItemFromCourse = async (itemId) => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: patient?.result?.id?.value || 0,
        action: "remove_item_from_course",
        service_id: itemId,
      });
      setToast({
        title: data?.message || t("plan.toast.courseItemRemoved"),
        status: data?.status || "success",
      });
      if (data?.status !== "error") {
        await logPlanChange("patient_plan_course_item_removed", {
          idItem: itemId,
          type: "remove_item_from_course",
          details: t("plan.log.serviceId", { id: itemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления услуги из курса:", err);
      setToast({
        title: t("plan.toast.courseItemRemoveFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Полное удаление услуги из плана лечения */
  const handleDeleteService = async (serviceItemId) => {
    if (blocked) return;
    if (!window.confirm(t("plan.toast.confirmDeleteService"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        action: "delete_service",
        service_id: serviceItemId,
      });
      setToast({
        title: data?.message || t("plan.toast.serviceDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_deleted", {
          idItem: serviceItemId,
          type: "delete_service",
          details: getServiceNameByItemId(serviceItemId) || t("plan.log.serviceId", { id: serviceItemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления услуги:", err);
      setToast({
        title: t("plan.toast.serviceDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Удаление подуслуги из состава услуги */
  const handleDeleteSubitem = async (subitemId) => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        action: "delete_subitem",
        id_subitem: subitemId,
      });
      setToast({
        title: data?.message || t("plan.toast.subitemDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_subitem_deleted", {
          idItem: subitemId,
          type: "delete_subitem",
          details: t("plan.log.subitemId", { id: subitemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления подуслуги:", err);
      setToast({
        title: t("plan.toast.subitemDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const handleOpenModalAddCourse = () => {
    setShowModalAddCourse(true);
  };

  const handleSubmitAddCourse = async () => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: patient?.result?.id?.value || 0,
        action: "add_course",
        course_name: courseSelectedName,
        course_color: courseSelectedColor,
        item_ids: courseSelected,
      });
      setToast({
        title: data?.message || t("plan.toast.courseAdded"),
        status: data?.status || "success",
      });
      if (data?.status !== "error") {
        await logPlanChange("patient_plan_course_added", {
          type: "add_course",
          details: t("plan.log.courseDetails", {
            name: courseSelectedName || t("plan.log.courseNoName"),
            count: courseSelected.length,
          }),
        });
        const parsedRoutePlanId = Number(routePlanId);
        const currentPlanId =
          Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0
            ? parsedRoutePlanId
            : patient?.result?.id?.value || 0;
        try {
          const refreshedPlan = await GetData({
            module: "patient",
            block: "treatment_plan",
            id_patient: patientId,
            id_plan: currentPlanId,
          });
          if (isSuccessResponse(refreshedPlan)) {
            setPatient(refreshedPlan);
          } else {
            await applyPlanResponse(data);
          }
        } catch (refreshErr) {
          console.error("Ошибка перезагрузки текущего плана после добавления курса:", refreshErr);
          await applyPlanResponse(data);
        }
        setShowModalAddCourse(false);
        setCourseSelected([]);
        setCourseSelectedName("");
        setCourseSelectedColor(COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0");
      }
    } catch (err) {
      console.error("Ошибка добавления курса:", err);
      setToast({
        title: t("plan.toast.courseAddFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Удаление группы (со всеми её услугами) */
  const handleDeleteGroup = async (groupId) => {
    if (blocked) return;
    if (!window.confirm(t("plan.toast.confirmDeleteGroup"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: groupId,
        action: "delete_group",
      });
      setToast({
        title: data?.message || t("plan.toast.groupDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_group_deleted", {
          idItem: groupId,
          type: "delete_group",
          details: getGroupNameById(groupId) || t("plan.log.groupId", { id: groupId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления группы:", err);
      setToast({
        title: t("plan.toast.groupDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Полное удаление плана лечения */
  const handleDeletePlan = async () => {
    if (blocked) return;
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    if (!window.confirm(t("plan.toast.confirmDeletePlan"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "delete_plan",
      });
      setToast({
        title: data?.message || t("plan.toast.planDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        navigate(`/patient/${patientId}/plan`);
      }
    } catch (err) {
      console.error("Ошибка удаления плана:", err);
      setToast({
        title: t("plan.toast.planDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const fetchPlan = async ({
    id_plan = 0,
    id_group = 0,
    id_item = 0,
    id_appointment = 0,
    action = "",
    value = null,
    comment = "",
    service_id = null,
    group_type = null,
    showFullLoading = false,
    preserveScroll = true,
  } = {}) => {
    if (preserveScroll && typeof window !== "undefined") {
      lastScrollYRef.current = window.scrollY || 0;
      restoreScrollPendingRef.current = true;
    } else {
      restoreScrollPendingRef.current = false;
    }

    if (showFullLoading) setLoading(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan,
        id_group,
        id_item,
        id_appointment,
        action,
        value,
        comment,
        service_id,
        group_type,
      });
      await applyPlanResponse(data);
      console.log("data", data);
      setToast({
        title: data.message,
        status: data.status,
      });
      setBlocked(false);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      if (showFullLoading) setLoading(false);
    }
  };

  const handleIncrease = async (group) => {
    const groupId = group?.id?.value || 0;
    if (!groupId) return;
    if (blocked) return;
    setBlocked(true);
    try {
      const response = await fetchPlan(
        {
          id_plan: patient?.result?.id?.value || 0,
          id_group: groupId,
          action: "count_appointment_group",
          value: "plus",
          showFullLoading: false,
        }
      );
      if (isSuccessResponse(response)) {
        const responseGroup = (response?.result?.groups || []).find(
          (g) => String(g?.id?.value) === String(groupId),
        );
        await logPlanChange("patient_plan_appointment_count_increased", {
          idItem: groupId,
          type: "count_appointment",
          details: `${group?.name?.value || t("plan.log.groupFallback", { id: groupId })}: ${responseGroup?.count_appointment?.value ?? "—"}`,
        });
      }
    } finally {
      setBlocked(false);
    }
  };

  const handleDecrease = async (group) => {
    const groupId = group?.id?.value || 0;
    if (!groupId) return;
    if (blocked) return;
    setBlocked(true);
    try {
      const response = await fetchPlan(
        {
          id_plan: patient?.result?.id?.value || 0,
          id_group: groupId,
          action: "count_appointment_group",
          value: "minus",
          showFullLoading: false,
        }
      );
      if (isSuccessResponse(response)) {
        const responseGroup = (response?.result?.groups || []).find(
          (g) => String(g?.id?.value) === String(groupId),
        );
        await logPlanChange("patient_plan_appointment_count_decreased", {
          idItem: groupId,
          type: "count_appointment",
          details: `${group?.name?.value || t("plan.log.groupFallback", { id: groupId })}: ${responseGroup?.count_appointment?.value ?? "—"}`,
        });
      }
    } finally {
      setBlocked(false);
    }
  };

  const handlePlanStatusChange = async (nextStatus) => {
    if (blocked) return;
    const currentStatus = patient?.result?.status?.value;
    if (!nextStatus || nextStatus === currentStatus) return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "change_status_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        new_status_plan: nextStatus,
      });

      if (!isSuccessResponse(data)) {
        setToast({
          title: data?.message || t("plan.toast.statusChangeFailed"),
          status: "error",
        });
        return;
      }

      await applyPlanResponse(data);
      setToast({
        title: data?.message || t("plan.toast.statusChanged"),
        status: data?.status || "success",
      });
      await logPlanChange("patient_plan_status_changed", {
        type: "status",
        details: t("plan.log.newStatus", {
          status:
            nextStatus === "active"
              ? t("plan.header.statusActive")
              : t("plan.header.statusArchived"),
        }),
      });
    } catch (err) {
      console.error("Ошибка изменения статуса плана лечения:", err);
      setToast({
        title: t("plan.toast.statusChangeFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  // Карта id курса -> цвет для подсветки строк услуг
  const courseIdToColor = React.useMemo(() => {
    const map = {};
    (patient?.result?.courses || []).forEach((c) => {
      const id = c?.id?.value;
      const color = c?.color?.value;
      if (id && color) map[id] = color;
    });
    return map;
  }, [patient?.result?.courses]);

  // Карта id курса -> название (для заголовка курса в таблице)
  const courseIdToName = React.useMemo(() => {
    const map = {};
    (patient?.result?.courses || []).forEach((c) => {
      const id = c?.id?.value;
      const name = c?.name?.value;
      if (id && name) map[id] = name;
    });
    return map;
  }, [patient?.result?.courses]);

  // Функция для получения максимального номера приема
  const getMaxAppointmentNumber = (groups) => {
    let maxNumber = 0;
    groups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.appointments) {
          item.appointments.forEach((appointment) => {
            const appointmentNumber = parseInt(
              appointment.appointment_number.value,
            );
            if (appointmentNumber > maxNumber) {
              maxNumber = appointmentNumber;
            }
          });
        }
      });
    });
    return maxNumber;
  };

  // Функция для рендеринга статуса приема по номеру
  const renderAppointmentStatus = (appointments, appointmentNumber) => {
    if (appointments) {
      const appointment = appointments.find(
        (apt) => parseInt(apt.appointment_number.value) === appointmentNumber,
      );
      if (appointment) {
        return appointment.status.value === "scheduled" ? "filled" : "empty";
      }
    }
    return "empty";
  };

  const getAppointmentForItem = (item, appointmentNumber) => {
    if (!item?.appointments) return null;
    return item.appointments.find(
      (apt) => parseInt(apt?.appointment_number?.value, 10) === appointmentNumber,
    ) || null;
  };

  const getMarkedAppointmentsCountForItem = (item) => {
    if (!item?.appointments) return 0;

    return item.appointments.filter((apt) => {
      const hasAdmission = apt?.id_admission?.value !== undefined && String(apt.id_admission.value) !== "0";
      const isScheduled = apt?.status?.value === "scheduled";
      return hasAdmission || isScheduled;
    }).length;
  };

  // Проверка: в данной группе приём с номером отмечен — либо есть id_admission (отметили в "Список приёмов"),
  // либо есть запись назначения со status "scheduled" (отметили ячейку в плане)
  const isAppointmentMarkedInGroup = (group, appointmentNumber) => {
    if (!group?.items) return false;
    for (const item of group.items) {
      const apt = (item.appointments || []).find(
        (a) => parseInt(a.appointment_number?.value) === appointmentNumber,
      );
      if (!apt) continue;
      const hasAdmission = apt.id_admission?.value !== undefined && String(apt.id_admission.value) !== "0";
      const isScheduled = apt.status?.value === "scheduled";
      if (hasAdmission || isScheduled) return true;
    }
    return false;
  };

  // Получить информацию о приёме (patient_admission) для группы и номера приёма
  const getAdmissionInfoForGroupAppointment = (group, appointmentNumber) => {
    if (!patient?.result?.admission || !group?.items) return null;

    for (const item of group.items) {
      const apt = (item.appointments || []).find(
        (a) => parseInt(a.appointment_number?.value) === appointmentNumber,
      );
      if (!apt) continue;
      const admissionId = apt.id_admission?.value;
      if (!admissionId || String(admissionId) === "0") continue;

      const info = patient.result.admission[admissionId];
      if (info) return info;
    }

    return null;
  };

  // Форматирование даты в вид dd.mm.YYYY, пустые/нулевые даты отображаем как длинное тире
  const formatDateShort = (dateString) => {
    if (
      !dateString ||
      dateString === "0000-00-00 00:00" ||
      dateString === "0000-00-00 00:00:00"
    ) {
      return "—";
    }

    const [datePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) {
      return "—";
    }

    return `${day}.${month}.${year}`;
  };

  // Открыть модальное окно «Указать дату приёма»
  const handleOpenSetDateModal = (appointmentNumber, idTreatmentPlanGroup) => {
    setSetDateParams({ appointmentNumber, groupId: idTreatmentPlanGroup });
    setShowSetDateModal(true);
  };

  const handleOpenAppointmentCommentModal = (
    appointmentNumber,
    idTreatmentPlanGroup,
    itemId,
    currentComment = "",
  ) => {
    setAppointmentCommentParams({
      appointmentNumber,
      groupId: idTreatmentPlanGroup,
      itemId,
    });
    setAppointmentCommentValue(currentComment || "");
    setShowAppointmentCommentModal(true);
  };

  const handleOpenAppointmentCommentViewModal = (comment = "") => {
    setAppointmentCommentViewValue(String(comment || "").trim());
    setShowAppointmentCommentViewModal(true);
  };

  // Сохранить плановую дату приёма — создание записи в patient_admission с датой
  const handleSaveAdmissionDate = async (data) => {
    const { appointmentNumber, groupId } = setDateParams;
    if (blocked || !appointmentNumber || !groupId) return;
    const idPlan = patient?.result?.id?.value;
    if (!idPlan) return;
    setBlocked(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: idPlan,
        id_treatment_plan_group: groupId,
        appointment_number: appointmentNumber,
        date: data.date || "",
        action: "set_admission_date",
      });
      await applyPlanResponse(response);
      setShowSetDateModal(false);
      setSetDateParams({ appointmentNumber: null, groupId: null });
      setToast({
        title: response?.message || t("plan.toast.planDateSaved"),
        status: response?.status || "success",
      });
      if (isSuccessResponse(response)) {
        await logPlanChange("patient_plan_admission_date_set", {
          idItem: groupId,
          type: "set_admission_date",
          details: t("plan.log.appointmentDate", {
            number: appointmentNumber,
            date: data?.date || "—",
          }),
        });
      }
    } catch (err) {
      console.error("Ошибка сохранения даты приёма:", err);
      setToast({ title: t("plan.toast.planDateSaveFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  const handleSaveAppointmentComment = async (data) => {
    const { appointmentNumber, groupId, itemId } = appointmentCommentParams;
    if (blocked || !appointmentNumber || !groupId || !itemId) return;
    const idPlan = patient?.result?.id?.value;
    if (!idPlan) return;
    const commentValue = String(data?.comment ?? "").trim();
    setBlocked(true);
    try {
      const response = await fetchPlan({
        id_plan: idPlan,
        id_group: groupId,
        id_item: itemId,
        id_appointment: appointmentNumber,
        action: "appointment_comment",
        value: commentValue,
        comment: commentValue,
        showFullLoading: false,
      });
      if (isSuccessResponse(response)) {
        setShowAppointmentCommentModal(false);
        setAppointmentCommentParams({ appointmentNumber: null, groupId: null, itemId: null });
        setAppointmentCommentValue("");
      }
    } finally {
      setBlocked(false);
    }
  };

  // Функция для расчета скидки
  const calculateDiscount = (amountBase, amountTotal) => {
    const base = parseFloat(amountBase);
    const total = parseFloat(amountTotal);
    if (base > total) {
      const discount = (((base - total) / base) * 100).toFixed(0);
      return {
        hasDiscount: true,
        discountPercent: discount,
        discountAmount: base - total,
      };
    }
    return { hasDiscount: false };
  };

  useEffect(() => {
    if (!showModalServices) return;
    if (!selectedGroupType) return;

    const query = (serviceSearchQuery || "").trim();

    // не дергаем бек, пока не введут минимум 2 символа
    if (query.length < 2) {
      setServiceSearchResults([]);
      setServiceSearchLoading(false);
      setServiceSearchError(null);
      return;
    }

    setServiceSearchLoading(true);
    setServiceSearchError(null);

    const handle = setTimeout(async () => {
      try {
        const response = await GetData({
          module: "patient",
          block: "treatment_plan",
          id_patient: patientId,
          id_plan: patient?.result?.id?.value || 0,
          id_group: selectedGroupId || 0,
          action: "get_available_services",
          group_type: selectedGroupType,
          clinic_id: serviceClinicId ? Number(serviceClinicId) : undefined,
          q: query,
          limit: 50,
        });

        const services = response?.services || [];
        setServiceSearchResults(services);
      } catch (err) {
        console.error("Ошибка поиска услуг:", err);
        setServiceSearchError(err?.message || "search_error");
        setServiceSearchResults([]);
      } finally {
        setServiceSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showModalServices,
    selectedGroupType,
    selectedGroupId,
    serviceSearchQuery,
    serviceClinicId,
    patientId,
  ]);

  // Филиалы для фильтра услуг (один раз на вкладку плана лечения).
  useEffect(() => {
    let cancelled = false;

    const loadServiceFilials = async () => {
      try {
        const response = await GetData({
          module: "patient",
          block: "treatment_plan",
          action: "get_all_clinics",
        });
        if (cancelled) return;
        const filials = Array.isArray(response?.all_clinics)
          ? response.all_clinics
          : [];
        const myClinics = Array.isArray(response?.test?.my_clinics)
          ? response.test.my_clinics.map((id) => String(id))
          : [];
        setServiceFilials(filials);
        setServiceMyClinics(myClinics);
      } catch (err) {
        console.error("Ошибка загрузки филиалов для фильтра услуг:", err);
      }
    };

    loadServiceFilials();
    return () => {
      cancelled = true;
    };
  }, []);

  // Филиал, к которому привязан пользователь (таблица user_clinic). Если
  // привязок несколько — берём первый, который есть в списке филиалов.
  const userClinicId = useMemo(() => {
    const ids = serviceFilials.map((c) => String(c.id));
    return serviceMyClinics.find((id) => ids.includes(id)) || "";
  }, [serviceFilials, serviceMyClinics]);

  // Филиал по умолчанию: филиал пользователя (user_clinic) -> сохранённый -> все.
  useEffect(() => {
    if (!showModalServices) return;
    const ids = serviceFilials.map((c) => String(c.id));
    if (!ids.length) return;

    if (userClinicId && ids.includes(userClinicId)) {
      setServiceClinicId(userClinicId);
      return;
    }
    const saved = String(get("filters.plan.clinic_id", "") || "");
    setServiceClinicId(saved && ids.includes(saved) ? saved : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalServices, serviceFilials, serviceMyClinics, userClinicId]);

  useEffect(() => {
    setPlanName(patient?.result?.name?.value || "");
  }, [patient?.result?.name?.value]);

  useEffect(() => {
    const currentDate =
      patient?.result?.treatment_start_date?.value ||
      patient?.result?.date_start_treatment?.value ||
      patient?.result?.date_start?.value ||
      patient?.result?.start_date?.value ||
      "";
    setTreatmentStartDate(currentDate ? currentDate.split(" ")[0] : "");
  }, [
    patient?.result?.treatment_start_date?.value,
    patient?.result?.date_start_treatment?.value,
    patient?.result?.date_start?.value,
    patient?.result?.start_date?.value,
  ]);

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;
  if (!patient) return <div className="no-data">{t("plan.dataNotFound")}</div>;

  // Смена филиала в фильтре услуг: применяем и запоминаем в настройках.
  const handleServiceClinicChange = (value) => {
    setServiceClinicId(value);
    update({ filters: { plan: { clinic_id: value } } });
  };

  // Открытие модального окна и получение услуг текущей группы
  const handleModalOpenServices = async (groupType, groupId) => {
    setSelectedGroupType(groupType);
    setSelectedGroupId(groupId);
    setServiceSelectMode("add_service");
    setSelectedParentItemId(null);
    setServiceSearchQuery("");
    setServiceSearchResults([]);
    setServiceSearchError(null);
    setShowModalServices(true);
  };

  // Открытие модального окна выбора подуслуги для конкретной услуги
  const handleModalOpenSubitemServices = async (groupType, parentItemId) => {
    setSelectedGroupType(groupType);
    setSelectedGroupId(null);
    setServiceSelectMode("add_subitem");
    setSelectedParentItemId(parentItemId);
    setServiceSearchQuery("");
    setServiceSearchResults([]);
    setServiceSearchError(null);
    setShowModalServices(true);
  };

  //Добавление новой услуги
  const handleAddService = async (selectedService) => {
    if (!selectedService || !selectedGroupType || !selectedGroupId || blocked)
      return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: selectedGroupId,
        action: "add_service",
        service_id: selectedService.id,
        group_type: selectedGroupType,
      });

      await applyPlanResponse(data);
      setShowModalServices(false);
      setSelectedServiceId("");
      setServiceSearchQuery("");
      setServiceSearchResults([]);
      setToast({
        title: data?.message || t("plan.toast.serviceAdded"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_added", {
          idItem: selectedService.id,
          type: "add_service",
          details: selectedService.name || t("plan.log.serviceId", { id: selectedService.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления услуги:", err);
      setToast({ title: t("plan.toast.serviceAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // Открытие модального окна добавления своей (не из каталога) услуги
  const handleOpenModalCustomService = (groupType, groupId) => {
    setCustomServiceContext({ groupType, groupId });
    setCustomServiceName("");
    setShowModalCustomService(true);
  };

  //Добавление новой услуги только в план лечения (без каталога)
  const handleSubmitCustomService = async () => {
    if (blocked) return;
    const serviceName = customServiceName.trim();
    if (!serviceName || !customServiceContext.groupId) return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: customServiceContext.groupId,
        action: "add_service_custom",
        name: serviceName,
      });

      // Бэкенд подтверждает успех только статусом success. Пустой статус
      // означает, что действие не выполнено (например, бэкенд старой версии).
      if (!data || data.status !== "success") {
        console.warn("add_service_custom: неожиданный ответ бэкенда", data);
        setToast({
          title: data?.message || t("plan.toast.serviceAddFailed"),
          status: "error",
        });
        return;
      }

      await applyPlanResponse(data);
      setShowModalCustomService(false);
      setCustomServiceName("");
      setToast({
        title: data?.message || t("plan.toast.serviceAdded"),
        status: data?.status || "success",
      });
      await logPlanChange("patient_plan_service_added", {
        type: "add_service_custom",
        details: serviceName,
      });
    } catch (err) {
      console.error("Ошибка добавления новой услуги:", err);
      setToast({ title: t("plan.toast.serviceAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  //Добавление подуслуги к услуге
  const handleAddSubitem = async (selectedService) => {
    if (!selectedService || !selectedParentItemId || blocked) return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_item: selectedParentItemId,
        action: "add_subitem",
        service_id: selectedService.id,
      });

      await applyPlanResponse(data);
      setShowModalServices(false);
      setSelectedServiceId("");
      setSelectedParentItemId(null);
      setServiceSelectMode("add_service");
      setServiceSearchQuery("");
      setServiceSearchResults([]);
      setToast({
        title: data?.message || t("plan.toast.subitemAdded"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_subitem_added", {
          idItem: selectedService.id,
          type: "add_subitem",
          details: t("plan.log.subitemToService", {
            sub: selectedService.name || selectedService.id,
            parent:
              getServiceNameByItemId(selectedParentItemId) ||
              selectedParentItemId,
          }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления подуслуги:", err);
      setToast({ title: t("plan.toast.subitemAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };
  // Получение доступных групп для пациента
  const handleModalOpenGroup = async () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) {
      setToast({
        title: t("plan.toast.planNotDetermined"),
        status: "error",
      });
      return;
    }

    setLoadingGroup(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "get_available_group",
      });
      console.log("groups", response.groups);
      setAvailableGroup(response.groups || []);
      setShowModalGroup(true);
    } catch (err) {
      console.error("Ошибка загрузки группы услуг:", err);
      setToast({
        title: t("plan.toast.groupLoadFailed"),
        status: "error",
      });
      setShowModalGroup(false);
    } finally {
      setLoadingGroup(false);
    }
  };

  const handleAddGroup = async (selectedGroup) => {
    if (!selectedGroup || blocked) return;
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) {
      setToast({
        title: t("plan.toast.planNotDetermined"),
        status: "error",
      });
      return;
    }

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "add_group",
        id_group: selectedGroup.id,
      });

      await applyPlanResponse(data);
      setShowModalGroup(false);
      setSelectedGroupIdToAdd("");
      setToast({
        title: data?.message || t("plan.toast.groupAdded"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_group_added", {
          idItem: selectedGroup.id,
          type: "add_group",
          details: selectedGroup.name || t("plan.log.groupId", { id: selectedGroup.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления группы:", err);
      setToast({ title: t("plan.toast.groupAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // получение параметров услуги
  const handleModalChangeServices = async (service_id) => {
    setLoadingService(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        service_id: service_id,
        action: "get_parametrs_service",
      });

      setParamsService(response.result);
      setShowService(true);
    } catch (err) {
      console.error("Ошибка загрузки формы изменения услуги:", err);
      setToast({
        title: t("plan.toast.editFormLoadFailed"),
        status: "error",
      });
      setShowService(false);
    } finally {
      setLoadingService(false);
    }
  };

  // добавление изменений параметров услуги
  const handleSaveParamsService = async (updatedData) => {
    try {
      setBlocked(true);

      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "add_changes_service",
        service_id: paramsService.id,
        amount_base: updatedData.amount_base,
        name: updatedData.name,
        composition: updatedData.composition,
        technology: updatedData.technology,
        sort: updatedData.sort,
      });

      await applyPlanResponse(data);
      setShowService(false);
      setToast({
        title: data?.message || t("plan.toast.serviceChanged"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_updated", {
          idItem: paramsService.id,
          type: "add_changes_service",
          details: updatedData?.name || getServiceNameByItemId(paramsService.id) || t("plan.log.serviceId", { id: paramsService.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка изменения услуги:", err);
      setToast({ title: t("plan.toast.serviceChangeFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // Открытие модального окна и получение клиник
  const handleModalOpenPay = async (id_treatment_plan) => {
    setBlocked(true);
    try {
      const [clinicsResponse, staffResponse] = await Promise.all([
        GetData({
          module: "patient",
          block: "treatment_plan",
          action: "get_all_clinics",
        }),
        GetData({
          module: "staff",
          block: "list",
        }),
      ]);

      const rawStaff = staffResponse?.result || [];
      setPayModalStaff(
        rawStaff.map((s) => ({
          id: s.id?.value ?? s.id,
          name: s.name?.value ?? s.name ?? "",
        }))
      );

      setPaymentGroupId(id_treatment_plan);
      setAvailableClinics(clinicsResponse.all_clinics || []);
      setShowModalPay(true);
    } catch (err) {
      console.error("Ошибка загрузки клиник:", err);
      setToast({ title: t("plan.toast.clinicsLoadFailed"), status: "error" });
    } finally {
      setLoadingGroup(false);
    }
  };
  //Добавление оплаты
  const handleSavePay = async (paymentData) => {
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "create_transaction",
        id_patient: paymentData.id_patient,
        id_clinic: paymentData.id_clinic,
        id_group: paymentData.id_treatment_plan,
        payment_method: paymentData.payment_method,
        status: paymentData.status,
        price: paymentData.price,
        ...(paymentData.id_user_owner
          ? { id_user_owner: paymentData.id_user_owner }
          : {}),
      });

      setToast({
        title: data?.message || t("plan.toast.paymentAdded"),
        status: data?.status || "success",
      });
      setShowModalPay(false);
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_payment_added", {
          idItem: paymentData.id_treatment_plan,
          idClinic: paymentData.id_clinic,
          type: "create_transaction",
          details: t("plan.log.paymentAmount", { amount: paymentData.price }),
        });
        const parsedRoutePlanId = Number(routePlanId);
        const currentPlanId =
          Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0
            ? parsedRoutePlanId
            : patient?.result?.id?.value || 0;
        try {
          const refreshedPlan = await GetData({
            module: "patient",
            block: "treatment_plan",
            id_patient: patientId,
            id_plan: currentPlanId,
          });
          if (isSuccessResponse(refreshedPlan)) {
            setPatient(refreshedPlan);
          } else {
            await applyPlanResponse(data);
          }
        } catch (refreshErr) {
          console.error("Ошибка перезагрузки текущего плана после оплаты:", refreshErr);
          await applyPlanResponse(data);
        }
      } else {
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка добавления оплаты:", err);
      setToast({ title: t("plan.toast.paymentAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };
  // const handleGoToPrint = () => {
  //   const currentPlanId = getCurrentPlanId();
  //   if (!currentPlanId) return;
  //   navigate(`/patient/${patientId}/plan/${currentPlanId}/print`);
  // };

  const handleGoToPrintOtherFormat = () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    navigate(`/patient/${patientId}/plan/${currentPlanId}/print-other`);
  };

  const handleGoToAppointments = () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    navigate(`/patient/${patientId}/plan/${currentPlanId}/appointments`);
  };

  const handleGoToRecommendations = () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    navigate(`/patient/${patientId}/plan/${currentPlanId}/recommendations`);
  };

  const handlePlanNameSave = async () => {
    const currentName = patient?.result?.name?.value || "";
    const nextName = planName.trim();

    if (!nextName || nextName === currentName) return;

    try {
      const data = await GetData({
      module: "patient",
      block: "treatment_plan",
      action: "rename_plan",
      id_patient: patientId,
      id_plan: patient?.result?.id?.value || 0,
      new_name_plan: nextName,
      });

      if (isSuccessResponse(data)) {
        await applyPlanResponse(data);
        setToast({
          title: data?.message || t("plan.toast.planNameUpdated"),
          status: data?.status || "success",
        });
      } else {
        setToast({
          title: data?.message || t("plan.toast.planNameUpdateFailed"),
          status: "error",
        });
        setPlanName(currentName);
      }
    } catch (err) {
      console.error("Ошибка обновления названия плана:", err);
      setToast({
        title: t("plan.toast.planNameUpdateFailed"),
        status: "error",
      });
      setPlanName(currentName);
    }
  };

  const handleTreatmentStartDateSave = async () => {
    const currentDateRaw = patient?.result?.date_start?.value || "";
    const currentDate = currentDateRaw ? currentDateRaw.split(" ")[0] : "";
    const nextDate = treatmentStartDate;

    if (!nextDate || nextDate === currentDate) return;

    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "set_treatment_start_date",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        treatment_start_date: nextDate,
      });

      if (isSuccessResponse(data)) {
        await applyPlanResponse(data);
        setToast({
          title: data?.message || t("plan.toast.startDateUpdated"),
          status: data?.status || "success",
        });
      } else {
        setToast({
          title: data?.message || t("plan.toast.startDateUpdateFailed"),
          status: "error",
        });
        setTreatmentStartDate(currentDate);
      }
    } catch (err) {
      console.error("Ошибка обновления даты начала лечения:", err);
      setToast({
        title: t("plan.toast.startDateUpdateFailed"),
        status: "error",
      });
      setTreatmentStartDate(currentDate);
    }
  };

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return "—";
    const [datePart] = String(isoDate).trim().split(/[ T]/);
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "—";
    return `${day}.${month}.${year}`;
  };

  console.log("PATIENT:", patient);

  const saveIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
          fill="currentColor"
        />
      </svg>
      {t("plan.buttons.saveChanges")}
    </span>
  );

  const closeIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M6 6L18 18M18 6L6 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {t("plan.buttons.close")}
    </span>
  );

  /*
  const printerIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        width="20"
        height="20"
        style={{
          fill: "currentColor",
          transform: "scale(1.2)",
          transformOrigin: "center",
        }}>
        <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
      </svg>
    </span>
  );
  */

  const printerOtherFormatIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        width="20"
        height="20"
        style={{ color: "currentColor" }}>
        <path
          d="M6 9V3h12v6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6 14h12v8H6z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 17h8M8 20h5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      {t("plan.buttons.print")}
    </span>
  );

  const editIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        width="20"
        height="20"
        style={{ fill: "currentColor" }}>
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
      </svg>
      {t("plan.buttons.edit")}
    </span>
  );

  const deletePlanIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        width="20"
        height="20"
        style={{ fill: "currentColor" }}>
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
      </svg>
      {t("plan.buttons.deletePlan")}
    </span>
  );

  const backIcon = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        width="20"
        height="20"
        style={{ color: "currentColor" }}>
        <path
          d="M9 14L4 9l5-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4 9h11a4 4 0 1 1 0 8H9"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {t("plan.buttons.backToList")}
    </span>
  );

  const titleButtons = [
    {
      label: t("plan.buttons.appointmentsList"),
      onClick: handleGoToAppointments,
      type: "light",
    },
    {
      label: t("plan.buttons.recommendations"),
      onClick: handleGoToRecommendations,
      type: "light",
    },
    {
      label: backIcon,
      onClick: () => navigate(`/patient/${patientId}/plan`),
      type: "normal icon",
    },
  ];

  const printButtons = [
    // {
    //   label: printerIcon,
    //   onClick: handleGoToPrint,
    //   type: "light icon",
    // },
    {
      label: printerOtherFormatIcon,
      onClick: handleGoToPrintOtherFormat,
      type: "light icon",
    },
  ];

  const planActionButtons = coursesMode
    ? [
        ...printButtons,
        {
          label: closeIcon,
          onClick: handleCoursesClose,
          type: "normal icon",
        },
      ]
    : changed
    ? [
        {
          label: saveIcon,
          onClick: handleChangedClose,
          type: "success icon",
        },
      ]
    : [
        {
          label: t("plan.buttons.courses"),
          onClick: handleCoursesOpen,
          type: "light",
        },
        ...printButtons,
        {
          label: editIcon,
          onClick: handleChangedOpen,
          type: "light icon",
        },
        {
          label: deletePlanIcon,
          onClick: handleDeletePlan,
          type: "danger icon",
        },
      ];

  return (
    <>
      {toast.title ? (
        <Toast title={toast.title} status={toast.status} />
      ) : (
        false
      )}

      <div className="patient-plan">
          {/* Заголовок: название плана слева (как на других табах), кнопки справа */}
          <div className="patient-name-title-sticky">
            <BlockTitle
              showTitle
              title={
                changed ? (
                  <input
                    className="plan-name-input plan-name-input--title"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    onBlur={handlePlanNameSave}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    autoFocus
                  />
                ) : (
                  patient?.result?.name?.value || t("plan.list.defaultPlanName")
                )
              }
              buttons={titleButtons}
            />
          </div>

          <div className="plan-header">
            <div className="plan-meta">
              {!changed && (
                <div className="plan-meta-item">
                  <span className="plan-meta-label">{t("plan.header.total")}</span>
                  <span className="plan-meta-value">
                    <strong>
                      {planTotalToDisplay.toLocaleString(locale)} ₽
                    </strong>
                  </span>
                </div>
              )}

              <div className="plan-meta-item">
                <span className="plan-meta-label">{t("plan.header.statusLabel")}</span>
                <span className="plan-meta-value">
                  {changed ? (
                    <select
                      className="plan-status-select"
                      value={patient?.result?.status?.value || "active"}
                      onChange={(e) => handlePlanStatusChange(e.target.value)}
                      disabled={blocked}>
                      <option value="active">{t("plan.header.statusActive")}</option>
                      <option value="cancelled">{t("plan.header.statusArchived")}</option>
                    </select>
                  ) : (
                    <strong>
                      {patient?.result?.status?.value === "active"
                        ? t("plan.header.statusActive")
                        : t("plan.header.statusArchived")}
                    </strong>
                  )}
                </span>
              </div>

              {!changed && (
                <div className="plan-meta-item">
                  <span className="plan-meta-label">{t("plan.header.createdAt")}</span>
                  <span className="plan-meta-value">
                    <strong>{formatDateForDisplay(patient?.result?.date_created?.value) || "—"}</strong>
                  </span>
                </div>
              )}

              <div className="plan-meta-item">
                <span className="plan-meta-label">{t("plan.header.startDateShort")}</span>
                <span className="plan-meta-value">
                  {changed ? (
                    <DateInput
                      id="treatment-start-date"
                      className="form-control"
                      value={treatmentStartDate}
                      name="treatmentStartDate"
                      onChange={(e) => setTreatmentStartDate(e.target.value)}
                      onBlur={handleTreatmentStartDateSave}
                      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    />
                  ) : (
                    <strong>{formatDateForDisplay(patient?.result?.date_start?.value) || "—"}</strong>
                  )}
                </span>
              </div>

              {(allergyAnamnesisLine || patient?.allergics?.value) && (
                <div className="plan-meta-item plan-meta-item--wide">
                  <span className="plan-meta-label">{t("plan.header.allergics")}</span>
                  <span className="plan-meta-value plan-meta-allergics">
                    {allergyAnamnesisLine || patient?.allergics?.value || "—"}
                  </span>
                </div>
              )}
            </div>
            <div className="plan-header-actions">
              <div className="plan-view-switch" role="group">
                <button
                  type="button"
                  className={`plan-view-switch__btn${viewMode === "table" ? " is-active" : ""}`}
                  aria-pressed={viewMode === "table"}
                  onClick={() => changeViewMode("table")}>
                  {t("plan.matrix.viewTable")}
                </button>
                <button
                  type="button"
                  className={`plan-view-switch__btn${viewMode === "matrix" ? " is-active" : ""}`}
                  aria-pressed={viewMode === "matrix"}
                  onClick={() => changeViewMode("matrix")}>
                  {t("plan.matrix.viewMatrix")}
                </button>
              </div>
              {planActionButtons.map((button, index) => (
                <div
                  key={index}
                  onClick={button.onClick}
                  className={["button-default", button.type].filter(Boolean).join(" ")}>
                  {button.label}
                </div>
              ))}
            </div>
          </div>

          {(() => {
            // В режиме редактирования всегда показываем таблицу —
            // схема-матрица только для просмотра.
            const groupsBlock = viewMode === "matrix" && !changed ? (
              <PatientPlanMatrix
                groups={patient?.result?.groups || []}
                planCountAppointment={patient?.result?.count_appointment?.value}
                planId={patient?.result?.id?.value}
              />
            ) : (
              <>
          <div className="plan-groups">
            {(patient?.result?.groups || []).map((group, groupIndex) => {
              const groupEffectiveTotal = getGroupEffectiveTotal(group);
              const groupAppointmentLimit = getGroupAppointmentLimit(group);
              const appointmentHeaderType = getAppointmentHeaderType(group);
              const groupAppointmentNumbers = Array.from(
                { length: groupAppointmentLimit },
                (_, i) => i + 1,
              );
              const discountInfo = calculateDiscount(
                group?.amount_base?.value,
                groupEffectiveTotal,
              );
              const isWithoutAppointmentsGroup = isWithoutAppointmentsGroupByName(
                group?.name?.value,
              );
              const showCourseColumn = coursesMode && !isWithoutAppointmentsGroup;
              const showAppointmentColumns = !isWithoutAppointmentsGroup;

              return (
                <div key={group?.id?.value} className="page-block group-section">
                  {showAppointmentColumns && (
                    <div className="plan-count_appointment">
                      <div className="plan-name-count_appointment">
                        {t("plan.group.appointmentCount")}
                      </div>
                      <div className="plan-count_appointment-value">
                        {changed && (
                          <div
                            className="plan-minus-count_appointment"
                            onClick={() => handleDecrease(group)}>
                            -
                          </div>
                        )}
                        <div className="plan-result-count_appointment">
                          {groupAppointmentLimit ? groupAppointmentLimit : ""}
                        </div>
                        {changed && (
                          <div
                            className="plan-plus-count_appointment"
                            onClick={() => handleIncrease(group)}>
                            +
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {changed && showAppointmentColumns && (
                    <select
                        className="plan-column-type-select"
                        value={appointmentHeaderType}
                        disabled={blocked || !changed}
                        onChange={(event) =>
                          handleColumnTypeChange(group, event.target.value)
                        }>
                        <option value={1}>{t("plan.group.colTypeAppointments")}</option>
                        <option value={2}>{t("plan.group.colTypeConsecutiveDays")}</option>
                        <option value={3}>{t("plan.group.colTypeEvenDays")}</option>
                        <option value={4}>{t("plan.group.colTypeOddDays")}</option>
                      </select>
                    )}
                  {/* Таблица группы */}
                  {group?.items?.length > 0 ? (
                    <div className="table-wrapper">
                      <div className="group-table">
                        <table>
                          <thead>
                            <tr>
                              <th className="service-name">
                                {getGroupTypeLabel(t, group)}
                              </th>
                              {showCourseColumn && (
                                <th className="course-checkbox-header">{t("plan.group.courseColumn")}</th>
                              )}
                              {showAppointmentColumns ? (
                                groupAppointmentNumbers.map((number) => (
                                  <th key={number} className="appointment-header">
                                    {getAppointmentHeaderLabel(number, appointmentHeaderType)}
                                  </th>
                                ))
                              ) : (
                                <th className="service-composition-header">{t("plan.group.composition")}</th>
                              )}
                            </tr>
                            {/* <tr className="row-set-admission-date">
                              <th className="service-name"></th>
                              {coursesMode && (
                                <th className="course-checkbox-header"></th>
                              )}
                              {appointmentNumbers.map((number) => {
                                const isMarked = isAppointmentMarkedInGroup(
                                  group,
                                  number,
                                );
                                const admissionInfo =
                                  getAdmissionInfoForGroupAppointment(
                                    group,
                                    number,
                                  );
                                const planDateValue =
                                  admissionInfo?.date_plan?.value;
                                const hasPlanDate =
                                  planDateValue &&
                                  planDateValue !== "0000-00-00 00:00" &&
                                  planDateValue !== "0000-00-00 00:00:00";

                                const buttonDisabled = !hasPlanDate && !isMarked;

                                return (
                                  <th
                                    key={number}
                                    className="appointment-header">
                                    <div className="plan-admission-date">
                                      {formatDateShort(planDateValue)}
                                    </div>
                                    {changed && (
                                      <button
                                        type="button"
                                        className="btn-set-admission-date"
                                        disabled={buttonDisabled}
                                        title={
                                          buttonDisabled
                                            ? "Сначала отметьте приём в разделе «Список приёмов»"
                                            : ""
                                        }
                                        onClick={() =>
                                          !buttonDisabled &&
                                          handleOpenSetDateModal(
                                            number,
                                            group?.id?.value,
                                          )
                                        }>
                                        {hasPlanDate
                                          ? "Изменить дату приёма"
                                          : "Указать дату приёма"}
                                      </button>
                                    )}
                                  </th>
                                );
                              })}
                            </tr> */}
                          </thead>
                          <tbody>
                            {(() => {
                              const sortedItems = group.items || [];

                              const colSpan =
                                1 +
                                (showCourseColumn ? 1 : 0) +
                                (showAppointmentColumns ? groupAppointmentNumbers?.length || 0 : 1);

                              // Пустые курсы не отображаем: показываем заголовок только для курсов,
                              // у которых есть хотя бы одна услуга в текущей группе.
                              const courseIdsWithItems = new Set(
                                sortedItems
                                  .map((it) => it?.id_treatment_plan_course?.value)
                                  .filter((id) => id != null && String(id).trim() !== "" && String(id) !== "0"),
                              );

                              let lastCourseId = null;

                              return sortedItems.map((item) => {
                                const courseId = item?.id_treatment_plan_course?.value;
                                const isCourse = Boolean(courseId) && String(courseId) !== "0";
                                const rowStyle =
                                  isCourse && courseIdToColor[courseId]
                                    ? { backgroundColor: courseIdToColor[courseId] }
                                    : undefined;

                                const courseNameRaw = isCourse
                                  ? String(courseIdToName[courseId] ?? "").trim()
                                  : "";
                                const courseName =
                                  courseNameRaw && courseNameRaw !== "0" ? courseNameRaw : "";

                                const shouldShowCourseTitle =
                                  !isWithoutAppointmentsGroup &&
                                  isCourse &&
                                  courseName &&
                                  courseIdsWithItems.has(courseId) &&
                                  courseId !== lastCourseId;
                                if (shouldShowCourseTitle) lastCourseId = courseId;

                                return (
                                  <React.Fragment key={item?.id?.value}>
                                    {shouldShowCourseTitle && (
                                      <tr className="course-title-row" style={rowStyle}>
                                        <td className="service-name" colSpan={colSpan}>
                                          <strong>{t("plan.group.program", { name: courseName })}</strong>
                                        </td>
                                      </tr>
                                    )}

                                    {/* Основная услуга */}
                                    <tr className="main-service" style={rowStyle}>
                                  <td className="service-name">
                                    <div className="service-title">
                                      <div className="mb-1">
                                        {displayName(item)}
                                      </div>
                                    </div>
                                    {!isWithoutAppointmentsGroup && (
                                      <div className="service-composition">
                                        <div className="mb-1">
                                          {item?.composition?.value}
                                        </div>
                                      </div>
                                    )}
                                    {(item?.subitems || []).length > 0 && (
                                      <div className="service-subitems">
                                        {(item.subitems || []).map((sub) => (
                                          <div key={sub?.id?.value} className="service-subitem">
                                            {changed && (
                                              <button
                                                type="button"
                                                className="btn-subitem-delete"
                                                title={t("plan.group.deleteSubitem")}
                                                aria-label={t("plan.group.deleteSubitem")}
                                                onClick={() => handleDeleteSubitem(sub?.id?.value)}>
                                                ×
                                              </button>
                                            )}
                                            <div className="mb-1">
                                              {displayName(sub)}
                                            </div>
                                            <div className="mb-1">
                                              {sub?.composition?.value}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <div className="service-price">
                                      <div className="mb-1">
                                        {isWithoutAppointmentsGroup
                                          ? `${parseFloat(
                                              item?.amount_base?.value || 0,
                                            ).toLocaleString(locale)} ₽`
                                          : `${getMarkedAppointmentsCountForItem(item)} X ${parseFloat(
                                              (item?.amount_base_total?.value ?? item?.amount_base?.value) || 0,
                                            ).toLocaleString(locale)} ₽`}
                                      </div>
                                    </div>

                                    {changed && (
                                      <div className="service-actions">
                                        <div
                                          className="changed-button"
                                          onClick={() =>
                                            handleModalChangeServices(
                                              item?.id?.value,
                                            )
                                          }>
                                          {t("plan.group.change")}
                                        </div>
                                        <div
                                          className="changed-button"
                                          onClick={() =>
                                            handleModalOpenSubitemServices(
                                              group?.type?.value,
                                              item?.id?.value,
                                            )
                                          }>
                                          {t("plan.group.addToComposition")}
                                        </div>
                                        <div
                                          className="changed-button changed-button-delete"
                                          onClick={() =>
                                            handleDeleteService(
                                              item?.id?.value,
                                            )
                                          }>
                                          {t("plan.group.delete")}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  {showCourseColumn && (
                                    <td className="course-checkbox-cell">
                                      {courseId && courseId !== "0" ? (
                                        <button
                                          type="button"
                                          className="btn-remove-from-course"
                                          title={t("plan.group.removeFromCourse")}
                                          onClick={() =>
                                            handleRemoveItemFromCourse(item?.id?.value)
                                          }
                                          aria-label={t("plan.group.removeFromCourseShort")}>
                                          ×
                                        </button>
                                      ) : (
                                        <input
                                          type="checkbox"
                                          checked={courseSelected.includes(item?.id?.value)}
                                          onChange={() =>
                                            handleToggleCourseItem(item?.id?.value)
                                          }
                                        />
                                      )}
                                    </td>
                                  )}
                                  {showAppointmentColumns ? (
                                    groupAppointmentNumbers.map((number) => (
                                      <td
                                        key={number}
                                        className="appointment-cell">
                                        {(() => {
                                          const appointment = getAppointmentForItem(item, number);
                                          const appointmentComment = String(
                                            appointment?.comment?.value ?? "",
                                          ).trim();
                                          const appointmentStatus = renderAppointmentStatus(
                                            item.appointments,
                                            number,
                                          );
                                          const hasCommentOnFilledAppointment =
                                            appointmentStatus === "filled" && Boolean(appointmentComment);

                                          return (
                                            <div className="appointment-cell-content">
                                              {changed ? (
                                                <>
                                                  <span
                                                    onClick={async () => {
                                                      const response = await fetchPlan(
                                                        {
                                                          id_plan: patient?.result?.id?.value || 0,
                                                          id_group: group?.id?.value,
                                                          id_item: item?.id?.value,
                                                          id_appointment: number,
                                                          action: "appointment",
                                                          value: "check",
                                                          showFullLoading: false,
                                                        }
                                                      );
                                                      if (isSuccessResponse(response)) {
                                                        await logPlanChange("patient_plan_appointment_checked", {
                                                          idItem: item?.id?.value,
                                                          type: "appointment",
                                                          details: t("plan.log.serviceAppointment", {
                                                            service: item?.name?.value || item?.id?.value,
                                                            number,
                                                          }),
                                                        });
                                                      }
                                                    }}
                                                    className={`appointment-indicator ${appointmentStatus} ${hasCommentOnFilledAppointment ? "with-comment" : ""} changed`}
                                                  />
                                                  <button
                                                    type="button"
                                                    className="appointment-comment-button"
                                                    onClick={() =>
                                                      handleOpenAppointmentCommentModal(
                                                        number,
                                                        group?.id?.value,
                                                        item?.id?.value,
                                                        appointmentComment,
                                                      )
                                                    }>
                                                    {t("plan.buttons.note")}
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  <span
                                                    className={`appointment-indicator ${appointmentStatus} ${hasCommentOnFilledAppointment ? "with-comment" : ""}`}
                                                  />
                                                  {appointmentComment && (
                                                    <div
                                                      className="appointment-comment-text"
                                                      title={appointmentComment}
                                                      >
                                                      {appointmentComment}
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    ))
                                  ) : (
                                    <td className="service-composition-cell">
                                      <div className="mb-1">{item?.composition?.value || "—"}</div>
                                    </td>
                                  )}
                                </tr>
                              </React.Fragment>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="no-services-name">
                        {getGroupTypeLabel(t, group)}
                      </div>
                      <div className="no-services">{t("plan.group.noServicesInGroup")}</div>
                    </>
                  )}
                  {(changed || coursesMode) && (
                    <div className="btn-add-service">
                      {coursesMode && (
                        <button
                          className="btn-base-course"
                          onClick={handleOpenModalAddCourse}>
                          {t("plan.buttons.addCourse")}
                        </button>
                      )}
                      {changed && (
                        <div className="btn-add-service-dropdown">
                          <button
                            type="button"
                            className="btn-base btn-add-service-toggle"
                            aria-haspopup="menu"
                            aria-expanded={addServiceMenuGroupId === group?.id?.value}
                            onClick={() =>
                              setAddServiceMenuGroupId((prev) =>
                                prev === group?.id?.value ? null : group?.id?.value,
                              )
                            }>
                            <span>{t("plan.buttons.addService")}</span>
                            <svg
                              className={`btn-add-service-arrow${addServiceMenuGroupId === group?.id?.value ? " is-open" : ""}`}
                              viewBox="0 0 24 24"
                              width="16"
                              height="16"
                              aria-hidden="true">
                              <path d="M7 10l5 5 5-5z" fill="currentColor" />
                            </svg>
                          </button>
                          {addServiceMenuGroupId === group?.id?.value && (
                            <div className="btn-add-service-menu" role="menu">
                              <button
                                type="button"
                                className="btn-add-service-item"
                                role="menuitem"
                                onClick={() => {
                                  setAddServiceMenuGroupId(null);
                                  handleModalOpenServices(
                                    group?.type?.value,
                                    group?.id?.value,
                                  );
                                }}>
                                {t("plan.buttons.addServiceFromCatalog")}
                              </button>
                              <button
                                type="button"
                                className="btn-add-service-item"
                                role="menuitem"
                                onClick={() => {
                                  setAddServiceMenuGroupId(null);
                                  handleOpenModalCustomService(
                                    group?.type?.value,
                                    group?.id?.value,
                                  );
                                }}>
                                {t("plan.buttons.addServiceNew")}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Итог по группе */}
                  <div className="group-summary">
                    <div className="group-prices">
                      <div className="price-total">
                        {t("plan.group.total")}
                        {groupEffectiveTotal.toLocaleString(locale)}{" "}
                        ₽
                      </div>
                      {/* Цветная индикация оплаты */}
                      {(() => {
                        const total = groupEffectiveTotal;
                        const fact = parseFloat(group?.amount_fact?.value) || 0;
                        const percent = total > 0 ? (fact / total) * 100 : 0;

                        // let color = "red";
                        // if (percent >= 100) {
                        //   color = "green";
                        // } else if (percent > 0) {
                        //   color = "orange";
                        // }

                        let color ="#6c757d61"

                        return (
                          <div className="price-fact" style={{ color }}>
                            {t("plan.group.paid", {
                              amount: fact.toLocaleString(locale),
                            })}
                          </div>
                        );
                      })()}
                      <div className="price-base">
                        <div>
                          {(parseFloat(group?.amount_base?.value) || 0) >
                          groupEffectiveTotal ? (
                            <>
                              {t("plan.group.insteadOf")}
                              {parseFloat(
                                group.amount_base.value,
                              ).toLocaleString(locale)}{" "}
                              ₽
                            </>
                          ) : (
                            false
                          )}
                        </div>
                        <div>
                          <>
                            {t("plan.group.unpaid")}
                            {parseFloat(
                              group.amount_fact.value < groupEffectiveTotal
                                ? groupEffectiveTotal -
                                    group.amount_fact.value
                                : 0,
                            ).toLocaleString(locale)}{" "}
                            ₽
                          </>
                        </div>
                      </div>

                      <div className="price-reason-discount">
                        {t("plan.group.discountReason")}
                        {group.reason_discount?.value
                          ? group.reason_discount?.value
                          : "—"}
                      </div>
                      {!changed && (
                        <div className="btn-add-paid">
                          <button
                            className="btn-base"
                            onClick={() => handleModalOpenPay(group.id.value)}>
                            {t("plan.buttons.addPayment")}
                          </button>
                        </div>
                      )}
                    </div>
                    {changed && (
                      <div className="group-actions">
                        <button
                          type="button"
                          className="btn-base btn-delete-group"
                          onClick={() => handleDeleteGroup(group?.id?.value)}>
                          {t("plan.buttons.deleteGroup")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {changed && (
            <div className="btn-add-group">
              {/* Кнопка добавления группы */}
              <button
                className="button-default light"
                onClick={() => handleModalOpenGroup()}>
                {t("plan.buttons.addGroup")}
              </button>
            </div>
          )}
              </>
            );
            return planGroupsSlotEl
              ? createPortal(groupsBlock, planGroupsSlotEl)
              : groupsBlock;
          })()}        </div>

      <SearchSelectModal
        show={showModalServices}
        onHide={() => {
          setShowModalServices(false);
          setSelectedServiceId("");
          setServiceSelectMode("add_service");
          setSelectedParentItemId(null);
          setServiceSearchQuery("");
          setServiceSearchResults([]);
          setServiceSearchError(null);
        }}
        title={serviceSelectMode === "add_subitem" ? t("plan.modals.selectSubitem") : t("plan.modals.selectService")}
        headerContent={
          serviceFilials.length > 0 ? (
            <Form.Group controlId="serviceFilialFilter">
              <Form.Label className="mb-1">
                {t("plan.modals.branchLabel")}
              </Form.Label>
              <Form.Select
                value={serviceClinicId}
                onChange={(e) => handleServiceClinicChange(e.target.value)}>
                <option value="">{t("plan.modals.allBranches")}</option>
                {serviceFilials.map((clinic) => (
                  <option key={clinic.id} value={String(clinic.id)}>
                    {clinic.name ||
                      t("plan.modals.branchFallback", { id: clinic.id })}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null
        }
        query={serviceSearchQuery}
        onQueryChange={setServiceSearchQuery}
        loading={serviceSearchLoading}
        options={serviceSearchResults}
        onSelect={serviceSelectMode === "add_subitem" ? handleAddSubitem : handleAddService}
        optionKey={(s) => String(s.id)}
        optionLabel={(s) =>
          pickLocalizedValue(
            { name: { value: s.name }, name_en: { value: s.name_en } },
            "name",
            i18n.language,
          )
        }
        optionSubLabel={(s) => s.composition}
        minQueryLength={2}
        hint={
          serviceSearchError
            ? t("plan.modals.searchError")
            : t("plan.modals.searchHint")
        }
        emptyMessage={t("plan.modals.nothingFound")}
        loadingMessage={t("plan.modals.searching")}
      />
      <SelectModal
        show={showModalGroup}
        onHide={() => {
          setShowModalGroup(false);
          setSelectedGroupIdToAdd("");
        }}
        title={t("plan.modals.selectGroup")}
        loading={loadingGroup}
        options={availableGroup}
        value={selectedGroupIdToAdd}
        onChange={setSelectedGroupIdToAdd}
        onSelect={handleAddGroup}
        optionLabel={(g) => getGroupTypeLabel(t, g)}
        optionValue={(g) => String(g.id)}
        confirmButtonText={t("plan.modals.add")}
        emptyMessage={t("plan.modals.noGroupsAvailable")}
        loadingMessage={t("plan.modals.groupsLoading")}
      />

      <EditableModal
        show={showModalService}
        onHide={() => setShowService(false)}
        title={t("plan.modals.editService")}
        initialData={paramsService}
        fields={fields}
        onSave={handleSaveParamsService}
      />

      <EditableModal
        show={showSetDateModal}
        onHide={() => {
          setShowSetDateModal(false);
          setSetDateParams({ appointmentNumber: null, groupId: null });
        }}
        title={t("plan.modals.setPlanDate")}
        fields={[
          {
            name: "date",
            label: t("plan.modals.planDateField"),
            type: "date",
          },
        ]}
        initialData={{ date: "" }}
        onSave={handleSaveAdmissionDate}
      />

      <EditableModal
        show={showAppointmentCommentModal}
        onHide={() => {
          setShowAppointmentCommentModal(false);
          setAppointmentCommentParams({ appointmentNumber: null, groupId: null, itemId: null });
          setAppointmentCommentValue("");
        }}
        title={t("plan.modals.appointmentNote")}
        fields={[
          {
            name: "comment",
            label: t("plan.modals.noteField"),
            type: "textarea",
          },
        ]}
        initialData={{ comment: appointmentCommentValue }}
        onSave={handleSaveAppointmentComment}
      />

      <Modal
        show={showAppointmentCommentViewModal}
        onHide={() => {
          setShowAppointmentCommentViewModal(false);
          setAppointmentCommentViewValue("");
        }}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.appointmentNote")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: "pre-wrap" }}>
          {appointmentCommentViewValue || t("plan.modals.noteEmpty")}
        </Modal.Body>
      </Modal>

      <PaidModal
        show={showModalPay}
        onHide={() => setShowModalPay(false)}
        clinics={availableClinics}
        staff={payModalStaff}
        patientId={patientId}
        treatmentPlanId={paymentGroupId}
        defaultClinicId={userClinicId}
        onSave={handleSavePay}
      />

      {/* Модальное окно «Добавить курс» */}
      <Modal
        show={showModalAddCourse}
        onHide={() => setShowModalAddCourse(false)}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.addCourseTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseName")}</label>
            <input
              type="text"
              className="form-control"
              value={courseSelectedName}
              onChange={(e) => setCourseSelectedName(e.target.value)}
              placeholder={t("plan.modals.courseNamePlaceholder")}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseColor")}</label>
            <select
              className="form-select"
              value={courseSelectedColor}
              onChange={(e) => setCourseSelectedColor(e.target.value)}>
              {COURSE_COLOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseSelected")}</label>
            <ul className="list-unstyled mb-0">
              {courseSelected.length === 0 ? (
                <li className="text-muted">{t("plan.modals.courseNoSelected")}</li>
              ) : (
                courseSelected.map((id) => {
                  const item = (patient?.result?.groups || [])
                    .flatMap((g) => g.items || [])
                    .find((it) => it?.id?.value === id);
                  const name = displayName(item) || id;
                  return <li key={id}>{name}</li>;
                })
              )}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalAddCourse(false)}>
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button variant="primary" onClick={handleSubmitAddCourse}>
            {t("plan.modals.createCourse")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно «Новая услуга» (только в план лечения, без каталога) */}
      <Modal
        show={showModalCustomService}
        onHide={() => {
          setShowModalCustomService(false);
          setCustomServiceName("");
        }}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.addCustomServiceTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">
              {t("plan.modals.customServiceName")}
            </label>
            <input
              type="text"
              className="form-control"
              value={customServiceName}
              onChange={(e) => setCustomServiceName(e.target.value)}
              placeholder={t("plan.modals.customServiceNamePlaceholder")}
              autoFocus
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModalCustomService(false);
              setCustomServiceName("");
            }}>
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitCustomService}
            disabled={!customServiceName.trim()}>
            {t("plan.modals.createService")}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
}

export default PatientPlan;
